import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, SegmentedControl } from '@/components/UI';
import { trpc, trpcClient } from '@/api/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast } from '@/components/Layout/Toast';
import type { SortingState } from '@/components/UI/dataTableConfig';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import { usePageContext } from '@context/PageContext';
import type {
    AuditCandidate,
    AuditPack,
    AuditProfile,
    BundlePreviewResponse,
    ConfirmationPackTarget,
    ExperimentDurationSummary,
    ExperimentEntitySummary,
    ExperimentExecutionStatus,
    ExternalAuditApproval,
    ExternalAuditSessionResponse,
    ExternalAuditTool,
    OptimizerFilterKey,
    OptimizerResultRow,
    OptimizerResultSortBy,
    OptionObjective,
    OutcomeMode,
    OutcomeMetric,
    ScreenerOptimizerAiAnalysisObjective,
    SuggestedBundleRow,
    SupportedEntityType
} from './screenerOptimizer/types';
import {
    OPTIMIZER_RESULT_FILTERS,
    externalAiAuditCandidateLimit,
    externalAiAuditRobustLimit,
    externalToolLaunchUrls,
    quickAuditCandidateLimit,
    quickAuditRobustLimit
} from './screenerOptimizer/constants';
import {
    clamp01Number,
    createEmptyOptimizerFilterValues,
    experimentEntityId,
    extractOptimizerConditionKeys,
    formatDecimal,
    formatPct01,
    jaccardSimilaritySortedUnique,
    labelForExternalAuditTool,
    optimizerMaterializationProgress,
    parseOptimizerFilterValue,
    toNumber
} from './screenerOptimizer/helpers';
import FoldsModal from './screenerOptimizer/FoldsModal';
import RuleJsonModal from './screenerOptimizer/RuleJsonModal';
import PromoteResultModal from './screenerOptimizer/PromoteResultModal';
import PromoteBundleModal from './screenerOptimizer/PromoteBundleModal';
import ConfirmationPackModal from './screenerOptimizer/ConfirmationPackModal';
import BundlePreviewModal from './screenerOptimizer/BundlePreviewModal';
import SuggestedBundlesModal from './screenerOptimizer/SuggestedBundlesModal';
import RobustAuditModal from './screenerOptimizer/RobustAuditModal';
import FeatureImpactModal from './screenerOptimizer/FeatureImpactModal';
import JobsTableCard from './screenerOptimizer/JobsTableCard';
import JobConfigCard from './screenerOptimizer/JobConfigCard';
import {
    buildOptimizerJobInput,
    type OptimizerOptionOverrides,
    type OptimizerValidationSettings,
    type OptionLegField
} from './screenerOptimizer/jobConfigInput';
import ExternalAuditSection from './screenerOptimizer/ExternalAuditSection';
import ResultsSection from './screenerOptimizer/ResultsSection';
import FeaturesSection from './screenerOptimizer/FeaturesSection';
import JobStatsGrid from './screenerOptimizer/JobStatsGrid';
import AuditSummaryBanner from './screenerOptimizer/AuditSummaryBanner';
import JobDetailHeader from './screenerOptimizer/JobDetailHeader';
import OptimizerConfirmationsPanel from './screenerOptimizer/OptimizerConfirmationsPanel';

interface ScreenerOptimizerPanelProps {
    experimentId: number;
    experimentName: string;
    executionStatus: ExperimentExecutionStatus;
    strategyId?: number | null;
    entities: ExperimentEntitySummary[];
    durations: ExperimentDurationSummary[];
}

const OPTIMIZER_RESULTS_PAGE_SIZE = 50;
const FEATURE_IMPACT_RESULTS_LIMIT = 100;

const optimizerConfirmationNotice = (advisory?: { status: string; shouldWarn: boolean; message: string | null } | null) => {
    if (!advisory?.shouldWarn) return null;

    switch (advisory.status) {
        case 'confirmation_not_run':
            return 'Optimizer confirmation has not been run; use confirmation backtests only when you want optimizer/backtest parity evidence.';
        case 'confirmation_pending':
            return 'Optimizer confirmation is still queued or running.';
        case 'confirmation_not_passed':
            return 'The latest optimizer confirmation did not pass; review it before relying on optimizer parity.';
        case 'confirmation_unavailable':
            return 'Optimizer confirmation status is unavailable.';
        default:
            return advisory.message ?? 'Optimizer confirmation needs review.';
    }
};

export const ScreenerOptimizerPanel: React.FC<ScreenerOptimizerPanelProps> = ({
    experimentId,
    experimentName,
    executionStatus,
    strategyId,
    entities,
    durations
}) => {
    const { updateSelection } = usePageContext();

    const supportedEntityTypes = useMemo((): SupportedEntityType[] => {
        const set = new Set<SupportedEntityType>();

        for (const e of entities) {
            if (e.entityType === 'Asset') set.add('Asset');
            if (e.entityType === 'AssetIndex') set.add('AssetIndex');
            if (e.entityType === 'Cryptocurrency') set.add('Cryptocurrency');
        }

        return Array.from(set);
    }, [entities]);

    const parseOptionalPositiveInt = useCallback((value: string): number | undefined => {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const parsed = Number.parseInt(trimmed, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    }, []);

    const [entityType, setEntityType] = useState<SupportedEntityType>('Asset');
    const [duration, setDuration] = useState<string>('');
    const [horizonSeconds, setHorizonSeconds] = useState<number>(0);
    // Default to fixed_horizon, but for option strategies the backend preflight recommends strategy_exit
    // (the realized objective). The displayed mode reflects that recommendation until the user overrides; on
    // first override we seed local state from the recommendation (see seedOverrideFromRecommendationIfNeeded)
    // so tweaking only the metric/objective can never silently fall back to the raw fixed_horizon default.
    const [outcomeMode, setOutcomeMode] = useState<OutcomeMode>('fixed_horizon');
    const [outcomeModeUserOverridden, setOutcomeModeUserOverridden] = useState<boolean>(false);

    const [outcomeMetric, setOutcomeMetric] = useState<OutcomeMetric>('underlying_profit_percent');
    const [optionObjective, setOptionObjective] = useState<OptionObjective>('ev_per_signal');
    const [strategyExitStrategyId, setStrategyExitStrategyId] = useState<string>(strategyId ? String(strategyId) : '');
    const [strategyExitCandidateId, setStrategyExitCandidateId] = useState<string>('');
    const [optionLegOverrides, setOptionLegOverrides] = useState<OptimizerOptionOverrides>({});
    const [validationSettings, setValidationSettings] = useState<OptimizerValidationSettings>({});

    const [showAdvancedConfig, setShowAdvancedConfig] = useState<boolean>(false);
    const [advancedConfigEnabled, setAdvancedConfigEnabled] = useState(false);
    const [maxDepth, setMaxDepth] = useState<number>(3);
    const [binCount, setBinCount] = useState<number>(20);
    const [minSupport, setMinSupport] = useState<number>(50);
    const [minUniqueEntities, setMinUniqueEntities] = useState<number>(10);
    const [foldCount, setFoldCount] = useState<number>(5);
    const [maxFeatures, setMaxFeatures] = useState<number>(60);

    const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<'results' | 'features'>('results');

    useEffect(() => {
        updateSelection({ screenerOptimizerJobId: selectedJobId });
    }, [selectedJobId, updateSelection]);

    const [featureImpactModalOpen, setFeatureImpactModalOpen] = useState<boolean>(false);
    const [featureImpactTab, setFeatureImpactTab] = useState<'needleMovers' | 'univariate' | 'combos'>('needleMovers');
    const [featureImpactSelectedFeatureKey, setFeatureImpactSelectedFeatureKey] = useState<string | null>(null);

    const [ruleJsonModalResult, setRuleJsonModalResult] = useState<OptimizerResultRow | null>(null);
    const [foldsModalResultId, setFoldsModalResultId] = useState<number | null>(null);
    const [promoteModalResult, setPromoteModalResult] = useState<OptimizerResultRow | null>(null);
    const [replayConfirmationPendingResultId, setReplayConfirmationPendingResultId] = useState<number | null>(null);
    const [promotionName, setPromotionName] = useState<string>('');
    const [promotionDescription, setPromotionDescription] = useState<string>('');
    const [promotionIsActive, setPromotionIsActive] = useState<boolean>(true);
    const [promotionAttachToStrategy, setPromotionAttachToStrategy] = useState<boolean>(false);

    // Default to the optimizer's robustness score, not the persisted `rank` column.
    // Persisted ranks require a massive DB-wide UPDATE on job completion, which is too slow at scale.
    const [resultsSorting, setResultsSorting] = useState<SortingState>([{ id: 'selectionScore', desc: true }]);
    const [resultsPage, setResultsPage] = useState<number>(1);

    const [filtersDrawerOpen, setFiltersDrawerOpen] = useState<boolean>(false);
    const [filterValues, setFilterValues] = useState<Record<OptimizerFilterKey, string>>(createEmptyOptimizerFilterValues);

    const setFilterValue = useCallback((key: OptimizerFilterKey, value: string) => {
        setFilterValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilterValues(createEmptyOptimizerFilterValues());
    }, []);

    const [bundleSelectedResultIds, setBundleSelectedResultIds] = useState<Set<number>>(() => new Set());
    const bundleResultCacheRef = useRef<Map<number, OptimizerResultRow>>(new Map());
    const [bundlePromoteModalResultIds, setBundlePromoteModalResultIds] = useState<number[] | null>(null);
    const [bundlePromotionName, setBundlePromotionName] = useState<string>('');
    const [bundlePromotionDescription, setBundlePromotionDescription] = useState<string>('');
    const [bundlePromotionIsActive, setBundlePromotionIsActive] = useState<boolean>(true);
    const [bundlePromotionAttachToStrategy, setBundlePromotionAttachToStrategy] = useState<boolean>(false);

    const [bundlePreviewModalData, setBundlePreviewModalData] = useState<BundlePreviewResponse | null>(null);

    const [suggestBundlesModalOpen, setSuggestBundlesModalOpen] = useState<boolean>(false);
    const [suggestedBundles, setSuggestedBundles] = useState<SuggestedBundleRow[]>([]);

    const [auditModalOpen, setAuditModalOpen] = useState<boolean>(false);
    const [auditPack, setAuditPack] = useState<AuditPack | null>(null);
    const [externalAuditTool, setExternalAuditTool] = useState<ExternalAuditTool>('codex');
    const [analysisObjectiveOverride, setAnalysisObjectiveOverride] = useState<ScreenerOptimizerAiAnalysisObjective | null>(null);
    const [userGuidance, setUserGuidance] = useState<string>('');
    const [targetSignalRetentionPct, setTargetSignalRetentionPct] = useState<string>('');
    const [targetSignalRetentionTolerancePct, setTargetSignalRetentionTolerancePct] = useState<string>('10');
    const [restoredBriefRunId, setRestoredBriefRunId] = useState<number | null>(null);
    const [externalAuditSession, setExternalAuditSession] = useState<ExternalAuditSessionResponse | null>(null);

    const [confirmationPackTarget, setConfirmationPackTarget] = useState<ConfirmationPackTarget | null>(null);
    const [confirmationPackMaxEntities, setConfirmationPackMaxEntities] = useState<number>(500);
    const [confirmationPackBypassCache, setConfirmationPackBypassCache] = useState<boolean>(false);

    const [hideSimilarResults, setHideSimilarResults] = useState<boolean>(false);
    const [hideSimilarThreshold, setHideSimilarThreshold] = useState<string>('0.8');

    const sortBy = (resultsSorting.length > 0 ? resultsSorting[0].id : undefined) as OptimizerResultSortBy | undefined;
    const sortDirection = resultsSorting.length > 0 ? (resultsSorting[0].desc ? ('DESC' as const) : ('ASC' as const)) : undefined;

    // Parsed, query-ready filter values keyed by the backend input field names.
    const parsedFilters = useMemo(() => {
        const out: Partial<Record<OptimizerFilterKey, number>> = {};

        for (const def of OPTIMIZER_RESULT_FILTERS) {
            const parsed = parseOptimizerFilterValue(def.parse, filterValues[def.key]);
            if (parsed !== undefined) out[def.key] = parsed;
        }

        return out;
    }, [filterValues]);

    // Named derivations consumed by the bundle/audit flows below.
    const { minTradeCount } = parsedFilters;
    const { minHoldoutTradeCount } = parsedFilters;
    const { minOptionTradeCount } = parsedFilters;
    const { minOptionExecutionRate } = parsedFilters;
    const { minHoldoutLift } = parsedFilters;

    useEffect(() => {
        setResultsPage(1);
    }, [parsedFilters, selectedJobId, sortBy, sortDirection]);

    useEffect(() => {
        if (supportedEntityTypes.length === 0) return;

        if (supportedEntityTypes.includes('Asset')) {
            setEntityType('Asset');
            return;
        }

        setEntityType(supportedEntityTypes[0]);
    }, [supportedEntityTypes]);

    useEffect(() => {
        if (entityType === 'Cryptocurrency' && outcomeMetric === 'option_profit_percent') {
            setOutcomeMetric('underlying_profit_percent');
        }
    }, [entityType, outcomeMetric]);

    const defaultDuration = useMemo(() => {
        if (durations.length === 0) return '';
        const sorted = durations.slice().sort((a, b) => b.totalEntries - a.totalEntries);
        return sorted[0]?.duration ?? durations[0].duration;
    }, [durations]);

    const availableHorizonsForSelectedDuration = useMemo(() => {
        const durationRow = durations.find((d) => d.duration === duration);
        return durationRow?.horizons ?? [];
    }, [durations, duration]);

    const defaultHorizonForSelectedDuration = useMemo(() => {
        const durationRow = durations.find((d) => d.duration === duration);
        if (!durationRow || durationRow.horizons.length === 0) return 0;

        const sorted = durationRow.horizons
            .slice()
            .sort((a, b) => (durationRow.metrics[String(b)]?.totalEntries ?? 0) - (durationRow.metrics[String(a)]?.totalEntries ?? 0));

        return sorted[0] ?? durationRow.horizons[0] ?? 0;
    }, [durations, duration]);

    useEffect(() => {
        if (duration) return;
        if (!defaultDuration) return;
        setDuration(defaultDuration);
    }, [defaultDuration, duration]);

    useEffect(() => {
        if (!duration) return;
        if (availableHorizonsForSelectedDuration.length === 0) return;
        if (horizonSeconds && availableHorizonsForSelectedDuration.includes(horizonSeconds)) return;
        setHorizonSeconds(defaultHorizonForSelectedDuration);
    }, [availableHorizonsForSelectedDuration, defaultHorizonForSelectedDuration, duration, horizonSeconds]);

    useEffect(() => {
        setBundleSelectedResultIds(new Set());
        bundleResultCacheRef.current.clear();
        setBundlePromoteModalResultIds(null);
        setBundlePreviewModalData(null);
        setSuggestBundlesModalOpen(false);
        setSuggestedBundles([]);
        clearFilters();
        setAuditModalOpen(false);
        setAuditPack(null);
        setExternalAuditSession(null);
        setAnalysisObjectiveOverride(null);
        setUserGuidance('');
        setTargetSignalRetentionPct('');
        setTargetSignalRetentionTolerancePct('10');
        setRestoredBriefRunId(null);
    }, [selectedJobId, clearFilters]);

    // --- Queries ---

    const optimizerJobInput = useMemo(
        () =>
            buildOptimizerJobInput(
                {
                    experimentId,
                    entityType,
                    duration,
                    horizonSeconds,
                    // Only send the outcome config once the user has explicitly chosen it. When omitted, the
                    // backend applies the option-aware default (strategy_exit + option_profit_percent + resolved
                    // lineage for option strategies; fixed_horizon for stock). Sending it here would suppress that
                    // auto-default (autoDefaulted would never be true).
                    ...(outcomeModeUserOverridden
                        ? {
                              outcomeMode,
                              ...(outcomeMode === 'strategy_exit'
                                  ? {
                                        strategyId: parseOptionalPositiveInt(strategyExitStrategyId),
                                        exitCandidateId: parseOptionalPositiveInt(strategyExitCandidateId)
                                    }
                                  : {}),
                              outcomeMetric,
                              ...(outcomeMetric === 'option_profit_percent' ? { optionObjective } : {})
                          }
                        : {}),
                    ...(advancedConfigEnabled
                        ? {
                              maxDepth,
                              binCount,
                              minSupport,
                              minUniqueEntities,
                              foldCount,
                              maxFeatures,
                              ...validationSettings
                          }
                        : {})
                },
                optionLegOverrides
            ),
        [
            binCount,
            duration,
            entityType,
            experimentId,
            foldCount,
            horizonSeconds,
            maxDepth,
            maxFeatures,
            minSupport,
            minUniqueEntities,
            outcomeMode,
            outcomeModeUserOverridden,
            optionObjective,
            outcomeMetric,
            parseOptionalPositiveInt,
            advancedConfigEnabled,
            validationSettings,
            optionLegOverrides,
            strategyExitCandidateId,
            strategyExitStrategyId
        ]
    );

    const preflightEnabled =
        experimentId > 0 &&
        supportedEntityTypes.includes(entityType) &&
        duration.length > 0 &&
        Number.isFinite(horizonSeconds) &&
        horizonSeconds > 0 &&
        (outcomeMode === 'fixed_horizon' ||
            (parseOptionalPositiveInt(strategyExitStrategyId) !== undefined &&
                parseOptionalPositiveInt(strategyExitCandidateId) !== undefined));

    const preflightQuery = useQuery({
        ...trpc.screener.optimizer.preflight.queryOptions(optimizerJobInput),
        enabled: preflightEnabled
    });

    const preflight = preflightQuery.data?.preflight ?? null;

    // The backend applies the option-aware outcome default (strategy_exit + option_profit_percent +
    // resolved lineage for option strategies). The UI omits the outcome config from the request until the
    // user picks one (see optimizerJobInput), so the displayed mode/metric simply reflect the backend
    // recommendation until the user overrides — no synchronous state writes from an effect.
    const outcomeModeRecommendation = preflight?.outcomeModeResolution ?? null;

    const displayedOutcomeMode: OutcomeMode = outcomeModeUserOverridden
        ? outcomeMode
        : (outcomeModeRecommendation?.recommendedOutcomeMode ?? outcomeMode);

    const displayedOutcomeMetric: OutcomeMetric = outcomeModeUserOverridden
        ? outcomeMetric
        : (outcomeModeRecommendation?.recommendedOutcomeMetric ?? outcomeMetric);

    // On the user's FIRST override, promote the effective backend recommendation into local editable state
    // (mode, metric, and the resolved strategy-exit lineage). Without this, overriding only the metric/objective
    // would leave outcomeMode at its raw fixed_horizon default and the candidate fields blank — silently sending
    // a fixed_horizon job (and a blank candidate) for an option strategy the backend recommended strategy_exit for.
    const seedOverrideFromRecommendationIfNeeded = useCallback(() => {
        if (outcomeModeUserOverridden) {
            return;
        }

        const recommendation = outcomeModeRecommendation;

        if (!recommendation) {
            return;
        }

        setOutcomeMode(recommendation.recommendedOutcomeMode);
        setOutcomeMetric(recommendation.recommendedOutcomeMetric);

        if (recommendation.recommendedOutcomeMode === 'strategy_exit') {
            if (recommendation.resolvedStrategyId !== null) {
                setStrategyExitStrategyId(String(recommendation.resolvedStrategyId));
            }

            if (recommendation.resolvedExitCandidateId !== null) {
                setStrategyExitCandidateId(String(recommendation.resolvedExitCandidateId));
            }
        }
    }, [outcomeModeRecommendation, outcomeModeUserOverridden]);

    const handleOutcomeModeChange = useCallback(
        (value: OutcomeMode) => {
            seedOverrideFromRecommendationIfNeeded();
            setOutcomeModeUserOverridden(true);
            setOutcomeMode(value);
        },
        [seedOverrideFromRecommendationIfNeeded]
    );

    const handleOutcomeMetricChange = useCallback(
        (value: OutcomeMetric) => {
            seedOverrideFromRecommendationIfNeeded();
            setOutcomeModeUserOverridden(true);
            setOutcomeMetric(value);
        },
        [seedOverrideFromRecommendationIfNeeded]
    );

    const handleOptionObjectiveChange = useCallback(
        (value: OptionObjective) => {
            seedOverrideFromRecommendationIfNeeded();
            setOutcomeModeUserOverridden(true);
            setOptionObjective(value);
        },
        [seedOverrideFromRecommendationIfNeeded]
    );

    const handleStrategyChange = (value: string) => {
        seedOverrideFromRecommendationIfNeeded();
        setOutcomeModeUserOverridden(true);
        setStrategyExitStrategyId(value);
        setOptionLegOverrides({});
    };

    const handleExitCandidateChange = (value: string) => {
        seedOverrideFromRecommendationIfNeeded();
        setOutcomeModeUserOverridden(true);
        setStrategyExitCandidateId(value);
    };

    const handleOptionLegChange = (key: OptionLegField, value: number | null) => {
        seedOverrideFromRecommendationIfNeeded();
        setOutcomeModeUserOverridden(true);
        setOptionLegOverrides((previous) => ({ ...previous, [key]: value ?? undefined }));
    };

    const jobsQuery = useQuery({
        ...trpc.screener.optimizer.listJobsForExperiment.queryOptions({ experimentId }),
        enabled: experimentId > 0,
        refetchInterval: (query) => {
            const jobs = query.state.data?.jobs ?? [];

            const hasActive = jobs.some(
                (j) => j.status === 'queued' || j.status === 'materializing' || j.status === 'optimizing' || j.status === 'validating'
            );

            return hasActive ? 5000 : false;
        }
    });

    const jobs = jobsQuery.data?.jobs ?? [];

    const mcpClientReadinessQuery = useQuery({
        ...trpc.user.listMcpClientReadiness.queryOptions(),
        enabled: experimentId > 0,
        refetchInterval: 15000,
        staleTime: 10000
    });

    const selectedMcpReadiness = mcpClientReadinessQuery.data?.clients.find((client) => client.externalTool === externalAuditTool);

    useEffect(() => {
        if (jobs.length === 0) return;
        if (selectedJobId !== null) return;
        const firstCompleted = jobs.find((j) => j.status === 'completed');
        setSelectedJobId(firstCompleted?.id ?? jobs[0].id);
    }, [jobs, selectedJobId]);

    const selectedJob = useMemo(() => {
        if (selectedJobId === null) return null;
        return jobs.find((j) => j.id === selectedJobId) ?? null;
    }, [jobs, selectedJobId]);

    const selectedJobUsesOptionMetric = selectedJob?.outcomeMetric === 'option_profit_percent';

    // Mirror the backend default AI-analysis objective: option jobs default to balanced_options, everything
    // else to balanced. Derived during render (not synced from an effect); the user's explicit pick wins until
    // the selected job changes, where the override is cleared alongside the other per-job state resets.
    const analysisObjective: ScreenerOptimizerAiAnalysisObjective =
        analysisObjectiveOverride ?? (selectedJobUsesOptionMetric ? 'balanced_options' : 'balanced');

    const holdoutFractionLabel = selectedJob?.holdoutFraction ? formatPct01(selectedJob.holdoutFraction, 0) : '15%';

    // Filters relevant to the selected job (option-only filters drop out for non-option jobs).
    const visibleFilters = useMemo(
        () => OPTIMIZER_RESULT_FILTERS.filter((def) => !def.optionOnly || selectedJobUsesOptionMetric),
        [selectedJobUsesOptionMetric]
    );

    // Active = has a value AND is relevant to the selected job; drives the chips + button badge.
    const activeFilters = useMemo(
        () => visibleFilters.filter((def) => filterValues[def.key].trim() !== ''),
        [visibleFilters, filterValues]
    );

    const selectedJobUsesOptionEv =
        selectedJob?.outcomeMetric === 'option_profit_percent' && selectedJob.optionObjective === 'ev_per_signal';

    const selectedJobProgress = useMemo(() => {
        return selectedJob ? optimizerMaterializationProgress(selectedJob) : null;
    }, [selectedJob]);

    const entityIdsForEntityType = useCallback(
        (targetEntityType: SupportedEntityType): number[] => {
            const ids: number[] = [];
            const seen = new Set<number>();

            for (const entity of entities) {
                if (entity.entityType !== targetEntityType) continue;
                const id = experimentEntityId(entity);
                if (id === null || seen.has(id)) continue;
                seen.add(id);
                ids.push(id);
            }

            return ids;
        },
        [entities]
    );

    const confirmationPackEntityIds = useMemo(() => {
        if (!confirmationPackTarget) return [];
        return entityIdsForEntityType(confirmationPackTarget.entityType);
    }, [confirmationPackTarget, entityIdsForEntityType]);

    const resultsQuery = useQuery({
        ...trpc.screener.optimizer.listResultsForJob.queryOptions({
            jobId: selectedJobId ?? 0,
            page: resultsPage,
            pageSize: OPTIMIZER_RESULTS_PAGE_SIZE,
            ...parsedFilters,
            ...(sortBy !== undefined ? { sortBy } : {}),
            ...(sortDirection !== undefined ? { sortDirection } : {})
        }),
        enabled: selectedJobId !== null,
        refetchInterval: selectedJob?.status === 'optimizing' || selectedJob?.status === 'validating' ? 5000 : false
    });

    const results = resultsQuery.data?.results ?? [];
    const resultsTotalCount = resultsQuery.data?.totalCount ?? 0;

    useEffect(() => {
        for (const result of results) {
            if (bundleSelectedResultIds.has(result.id)) bundleResultCacheRef.current.set(result.id, result);
        }
    }, [bundleSelectedResultIds, results]);

    const confirmationsQuery = useQuery({
        ...trpc.screener.optimizer.listConfirmationsForJob.queryOptions({
            jobId: selectedJobId ?? 0,
            limit: 25
        }),
        enabled: selectedJobId !== null,
        refetchInterval: (query) => {
            const confirmations = query.state.data?.confirmations ?? [];
            const hasActive = confirmations.some(({ confirmation }) => ['pending', 'queued', 'running'].includes(confirmation.status));

            return hasActive ? 5000 : false;
        }
    });

    const confirmations = confirmationsQuery.data?.confirmations ?? [];

    const similarThresholdValue = useMemo(() => {
        const raw = hideSimilarThreshold.trim();
        if (raw === '') return 0.8;
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return 0.8;
        if (parsed > 1) return clamp01Number(parsed / 100);
        return clamp01Number(parsed);
    }, [hideSimilarThreshold]);

    const resultById = useMemo(() => {
        const map = new Map(bundleResultCacheRef.current);
        for (const r of results) map.set(r.id, r);
        return map;
    }, [bundleSelectedResultIds, results]);

    const conditionKeysByResultId = useMemo(() => {
        const map = new Map<number, string[]>();

        for (const r of results) {
            map.set(r.id, extractOptimizerConditionKeys(r.computedRuleSummary));
        }

        return map;
    }, [results]);

    const selectedBundleResultIdsByRank = useMemo(() => {
        const ids = Array.from(bundleSelectedResultIds);

        ids.sort((a, b) => {
            const scoreA = toNumber(resultById.get(a)?.selectionScore);
            const scoreB = toNumber(resultById.get(b)?.selectionScore);
            if (scoreA !== scoreB) return scoreB - scoreA;
            return a - b;
        });

        return ids;
    }, [bundleSelectedResultIds, resultById]);

    const resultsFilteredForDiversity = useMemo(() => {
        if (!hideSimilarResults) return results;
        if (bundleSelectedResultIds.size === 0) return results;

        const threshold = similarThresholdValue;
        const selectedConditionKeys: string[][] = [];

        for (const selectedId of bundleSelectedResultIds) {
            const selectedResult = resultById.get(selectedId);
            const keys = selectedResult ? extractOptimizerConditionKeys(selectedResult.computedRuleSummary) : undefined;
            if (keys && keys.length > 0) selectedConditionKeys.push(keys);
        }

        if (selectedConditionKeys.length === 0) return results;

        const selectedSignatures = new Set<string>();

        for (const keys of selectedConditionKeys) {
            selectedSignatures.add(keys.join('|'));
        }

        const filtered: OptimizerResultRow[] = [];

        for (const r of results) {
            if (bundleSelectedResultIds.has(r.id)) {
                filtered.push(r);
                continue;
            }

            const candidateKeys = conditionKeysByResultId.get(r.id);

            if (!candidateKeys || candidateKeys.length === 0) {
                filtered.push(r);
                continue;
            }

            if (threshold >= 0.999 && selectedSignatures.has(candidateKeys.join('|'))) {
                continue;
            }

            let maxSimilarity = 0;

            for (const selectedKeys of selectedConditionKeys) {
                const sim = jaccardSimilaritySortedUnique(candidateKeys, selectedKeys);
                if (sim > maxSimilarity) maxSimilarity = sim;
                if (maxSimilarity >= threshold) break;
            }

            if (maxSimilarity < threshold) filtered.push(r);
        }

        return filtered;
    }, [bundleSelectedResultIds, conditionKeysByResultId, hideSimilarResults, resultById, results, similarThresholdValue]);

    const displayIndexByResultId = useMemo(() => {
        const map = new Map<number, number>();
        const pageOffset = (resultsPage - 1) * OPTIMIZER_RESULTS_PAGE_SIZE;

        for (let i = 0; i < resultsFilteredForDiversity.length; i++) {
            map.set(resultsFilteredForDiversity[i].id, pageOffset + i + 1);
        }

        return map;
    }, [resultsFilteredForDiversity, resultsPage]);

    const hiddenSimilarCount = useMemo(() => {
        if (!hideSimilarResults) return 0;
        if (bundleSelectedResultIds.size === 0) return 0;
        return Math.max(0, results.length - resultsFilteredForDiversity.length);
    }, [bundleSelectedResultIds.size, hideSimilarResults, results.length, resultsFilteredForDiversity.length]);

    const activeAuditPack = useMemo(() => {
        if (!auditPack) return null;
        if (selectedJobId === null) return null;
        return auditPack.job.id === selectedJobId ? auditPack : null;
    }, [auditPack, selectedJobId]);

    const activeExternalAuditSession = useMemo(() => {
        if (!externalAuditSession) return null;
        if (selectedJobId === null) return null;
        return externalAuditSession.auditPack.job.id === selectedJobId ? externalAuditSession : null;
    }, [externalAuditSession, selectedJobId]);

    const auditCandidateByResultId = useMemo(() => {
        const map = new Map<number, AuditCandidate>();
        if (!activeAuditPack) return map;

        for (const candidate of activeAuditPack.candidates) {
            map.set(candidate.resultId, candidate);
        }

        return map;
    }, [activeAuditPack]);

    const holdoutInfoText = useMemo(() => {
        if (!selectedJob || selectedJob.status === 'failed' || !selectedJob.totalEntries) return null;

        const holdoutFraction = selectedJob.holdoutFraction ? toNumber(selectedJob.holdoutFraction) : 0;

        const estimatedHoldoutEntries =
            holdoutFraction > 0 ? Math.floor(selectedJob.totalEntries * holdoutFraction) : Math.floor(selectedJob.totalEntries * 0.15);

        const holdoutLabel = selectedJob.holdoutFraction ? formatPct01(selectedJob.holdoutFraction, 1) : '15.0%';
        const embargoLabel = selectedJob.embargoSeconds ? formatTimeHorizon(selectedJob.embargoSeconds) : '-';

        const winsorP = selectedJob.winsorPercentile ? Math.round(toNumber(selectedJob.winsorPercentile) * 100) : 95;
        const winsorCap = selectedJob.winsorMaxCap ? formatDecimal(selectedJob.winsorMaxCap, 0) : '100';

        return `Holdout: ${holdoutLabel} (~${estimatedHoldoutEntries.toLocaleString()} entries) • Embargo: ${embargoLabel} • Winsor: p${winsorP} cap ${winsorCap}`;
    }, [selectedJob]);

    const featureAnalysisQuery = useQuery({
        ...trpc.screener.optimizer.featureAnalysis.queryOptions({ jobId: selectedJobId ?? 0 }),
        enabled: selectedJobId !== null && (activeTab === 'features' || featureImpactModalOpen)
    });

    const features = featureAnalysisQuery.data?.features ?? [];

    const featureImpactSimpleQuery = useQuery({
        ...trpc.screener.optimizer.featureImpactSimple.queryOptions({ jobId: selectedJobId ?? 0 }),
        enabled: selectedJobId !== null && featureImpactModalOpen
    });

    const needleMovers = featureImpactSimpleQuery.data?.features ?? [];

    const featureImpactResultsQuery = useQuery({
        ...trpc.screener.optimizer.listResultsForJob.queryOptions({
            jobId: selectedJobId ?? 0,
            page: 1,
            pageSize: FEATURE_IMPACT_RESULTS_LIMIT,
            ...parsedFilters,
            ...(sortBy !== undefined ? { sortBy } : {}),
            ...(sortDirection !== undefined ? { sortDirection } : {})
        }),
        enabled: selectedJobId !== null && featureImpactModalOpen && featureImpactTab === 'combos'
    });

    const featureImpactResults = featureImpactResultsQuery.data?.results ?? [];

    const externalAuditSessionQuery = useQuery({
        ...trpc.screener.optimizer.externalAuditSession.queryOptions({
            jobId: selectedJobId ?? 0,
            externalTool: externalAuditTool
        }),
        enabled: selectedJobId !== null && selectedJob?.status === 'completed',
        refetchInterval: 5000,
        staleTime: 5000
    });

    const optionTimingEvidenceQuery = useQuery({
        ...trpc.screener.optimizer.optionTimingEvidenceReadiness.queryOptions({ jobId: selectedJobId ?? 0 }),
        enabled: selectedJobId !== null && selectedJob?.status === 'completed',
        staleTime: Number.POSITIVE_INFINITY
    });

    const activeExternalWorkbench = externalAuditSessionQuery.data?.workbench ?? activeExternalAuditSession?.workbench ?? null;
    const activeExternalAnalysisRun = externalAuditSessionQuery.data?.analysisRun ?? activeExternalAuditSession?.analysisRun ?? null;
    const activeExternalAnalysisPlan = externalAuditSessionQuery.data?.analysisPlan ?? activeExternalAuditSession?.analysisPlan ?? null;

    const activeExternalRecommendations =
        externalAuditSessionQuery.data?.recommendations ?? activeExternalAuditSession?.recommendations ?? [];
    const walkForwardEvaluations = externalAuditSessionQuery.data?.walkForwardEvaluations ?? [];
    const experimentWalkForwardExposure = externalAuditSessionQuery.data?.experimentWalkForwardExposure ?? null;

    const activeExternalPrompt = activeExternalWorkbench?.session?.prompt ?? activeExternalAuditSession?.agentPrompt ?? '';
    const optionTimingEvidenceReadiness = optionTimingEvidenceQuery.data?.readiness ?? null;
    const optionTimingEvidenceLoading = selectedJobUsesOptionMetric && optionTimingEvidenceQuery.isPending;

    useEffect(() => {
        if (!activeExternalAnalysisRun || restoredBriefRunId === activeExternalAnalysisRun.id) return;

        setUserGuidance(activeExternalAnalysisRun.userGuidance ?? '');
        setTargetSignalRetentionPct(
            activeExternalAnalysisRun.targetSignalRetentionPct === null ? '' : String(activeExternalAnalysisRun.targetSignalRetentionPct)
        );
        setTargetSignalRetentionTolerancePct(
            activeExternalAnalysisRun.targetSignalRetentionTolerancePct === null
                ? '10'
                : String(activeExternalAnalysisRun.targetSignalRetentionTolerancePct)
        );
        setRestoredBriefRunId(activeExternalAnalysisRun.id);
    }, [activeExternalAnalysisRun, restoredBriefRunId]);

    const externalApprovalByArtifactId = useMemo(() => {
        const map = new Map<number, ExternalAuditApproval>();

        for (const approval of activeExternalWorkbench?.approvals ?? []) {
            if (approval.approvalType !== 'promote_screener' || approval.artifactId === null) continue;
            if (!map.has(approval.artifactId)) map.set(approval.artifactId, approval);
        }

        return map;
    }, [activeExternalWorkbench?.approvals]);

    const jobRequestApprovalByArtifactId = useMemo(() => {
        const map = new Map<number, ExternalAuditApproval>();

        for (const approval of activeExternalWorkbench?.approvals ?? []) {
            if (approval.approvalType !== 'start_screener_optimizer_job' || approval.artifactId === null) continue;
            if (!map.has(approval.artifactId)) map.set(approval.artifactId, approval);
        }

        return map;
    }, [activeExternalWorkbench?.approvals]);

    // --- Mutations ---

    const createJobMutation = useMutation({
        mutationFn: (vars: { acknowledgeFixedHorizonProxy?: boolean }) =>
            trpcClient.screener.optimizer.createJob.mutate({
                ...optimizerJobInput,
                ...(vars.acknowledgeFixedHorizonProxy ? { acknowledgeFixedHorizonProxy: true } : {})
            }),
        onSuccess: (data) => {
            showToast(`Optimizer job created (jobId=${data.jobId})`, 'success');
            setSelectedJobId(data.jobId);
            void jobsQuery.refetch();
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to create optimizer job', 'error');
        }
    });

    // Explicitly choosing fixed_horizon on an option strategy creates a job whose results can never be
    // promoted (unpromotable entry-edge proxy) — the backend blocks creation without acknowledgment.
    const handleCreateJob = useCallback(() => {
        const isExplicitFixedHorizonOnOptionStrategy =
            outcomeModeUserOverridden && outcomeMode === 'fixed_horizon' && outcomeModeRecommendation?.isOptionStrategy === true;

        if (isExplicitFixedHorizonOnOptionStrategy) {
            const acknowledged = window.confirm(
                'Fixed-horizon mode on an option strategy scores a generic option marked at a fixed horizon and ignores the ' +
                    "strategy's real exit and option legs. The results are an exploratory entry-edge proxy and are BLOCKED from " +
                    'promotion. Run it anyway as an exploratory probe?'
            );

            if (!acknowledged) return;

            createJobMutation.mutate({ acknowledgeFixedHorizonProxy: true });
            return;
        }

        createJobMutation.mutate({});
    }, [createJobMutation, outcomeMode, outcomeModeRecommendation, outcomeModeUserOverridden]);

    const promoteResultMutation = useMutation({
        mutationFn: (input: { resultId: number; name: string; description: string | null; isActive: boolean }) =>
            trpcClient.screener.optimizer.promoteResult.mutate({
                resultId: input.resultId,
                name: input.name,
                description: input.description,
                isActive: input.isActive
            }),
        onError: (error: Error) => {
            showToast(error.message || 'Failed to promote optimizer result', 'error');
        }
    });

    const promoteBundleMutation = useMutation({
        mutationFn: (input: { jobId: number; resultIds: number[]; name: string; description: string | null; isActive: boolean }) =>
            trpcClient.screener.optimizer.promoteBundle.mutate({
                jobId: input.jobId,
                resultIds: input.resultIds,
                name: input.name,
                description: input.description,
                isActive: input.isActive
            }),
        onError: (error: Error) => {
            showToast(error.message || 'Failed to promote optimizer bundle', 'error');
        }
    });

    const previewBundleMetricsMutation = useMutation({
        mutationFn: (input: { jobId: number; resultIds: number[] }) => trpcClient.screener.optimizer.previewBundleMetrics.query(input),
        onSuccess: (data) => {
            setBundlePreviewModalData(data);
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to preview bundle', 'error');
        }
    });

    const suggestBundlesMutation = useMutation({
        mutationFn: (input: {
            jobId: number;
            candidateLimit?: number;
            maxBundles?: number;
            maxRulesPerBundle?: number;
            minTradeCount?: number;
            minHoldoutTradeCount?: number;
            minOptionTradeCount?: number;
            minOptionExecutionRate?: number;
            minHoldoutLift?: number;
        }) => trpcClient.screener.optimizer.suggestBundles.query(input),
        onSuccess: (data) => {
            setSuggestedBundles(data.suggestions);
            setSuggestBundlesModalOpen(true);
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to suggest bundles', 'error');
        }
    });

    const auditPackMutation = useMutation({
        mutationFn: (input: { jobId: number; profile: AuditProfile; candidateLimit: number; robustLimit: number }) =>
            trpcClient.screener.optimizer.auditPack.query(input),
        onSuccess: (data) => {
            setAuditPack(data.auditPack);
            setAuditModalOpen(true);
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to audit optimizer results', 'error');
        }
    });

    const externalAuditSessionMutation = useMutation({
        mutationFn: (input: {
            jobId: number;
            analysisObjective: ScreenerOptimizerAiAnalysisObjective;
            userGuidance?: string;
            targetSignalRetentionPct?: number;
            targetSignalRetentionTolerancePct?: number;
            candidateLimit: number;
            robustLimit: number;
            externalTool: ExternalAuditTool;
        }) => trpcClient.screener.optimizer.startExternalAuditSession.mutate(input),
        onSuccess: (data) => {
            setAuditPack(data.auditPack);
            setExternalAuditSession(data);

            showToast(
                data.aiWorkSession
                    ? `AI screener recommendation session ${data.aiWorkSession.id} created`
                    : 'AI screener recommendation session queued',
                'success'
            );
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to create external AI audit session', 'error');
        }
    });

    const decidePromotionApprovalMutation = useMutation({
        mutationFn: ({ approvalId, status }: { approvalId: number; status: 'approved' | 'rejected' | 'cancelled' }) =>
            trpcClient.screener.optimizer.decidePromotionApproval.mutate({ approvalId, status }),
        onSuccess: (approval) => {
            showToast(`Screener promotion approval ${approval.status}`, 'success');
            void externalAuditSessionQuery.refetch();
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to update screener promotion approval', 'error');
        }
    });

    const startApprovedOptimizerJobMutation = useMutation({
        mutationFn: (artifactId: number) => trpcClient.screener.optimizer.startApprovedOptimizerJob.mutate({ artifactId }),
        onSuccess: (result) => {
            showToast(`Started screener optimizer job (jobId=${result.jobId})`, 'success');
            void externalAuditSessionQuery.refetch();
            void jobsQuery.refetch();
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to start approved screener optimizer job', 'error');
        }
    });

    const attachToStrategyMutation = useMutation({
        mutationFn: (input: { screenerRuleId: number }) => {
            if (!strategyId) {
                throw new Error('No strategyId available for this experiment');
            }

            return trpcClient.screener.attachToStrategy.mutate({
                strategyId,
                screenerRuleId: input.screenerRuleId
            });
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to attach screener to strategy', 'error');
        }
    });

    const queueConfirmationPackMutation = useMutation({
        mutationFn: () => {
            if (!strategyId) throw new Error('No strategyId available for this experiment');
            if (!confirmationPackTarget) throw new Error('No screener rule selected for confirmation');
            if (confirmationPackEntityIds.length === 0) throw new Error('No experiment entities available for this entity type');

            return trpcClient.screener.queueConfirmationBacktestPack.mutate({
                strategyId,
                screenerRuleId: confirmationPackTarget.screenerRuleId,
                entityType: confirmationPackTarget.entityType,
                entityIds: confirmationPackEntityIds,
                maxEntities: confirmationPackMaxEntities,
                bypassCache: confirmationPackBypassCache
            });
        },
        onSuccess: (result) => {
            showToast(`Queued ${result.submittedJobs.toLocaleString()} confirmation backtests`, 'success');
            setConfirmationPackTarget(null);
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to queue confirmation backtests', 'error');
        }
    });

    const confirmStrategyExitReplayMutation = useMutation({
        mutationFn: (input: { resultId: number }) => {
            if (!selectedJob) throw new Error('Select an optimizer job first');
            if (selectedJob.outcomeMode !== 'strategy_exit')
                throw new Error('Replay confirmation is only available for strategy-exit jobs');

            const replayStrategyId = selectedJob.strategyId ?? strategyId ?? null;
            if (!replayStrategyId) throw new Error('No strategyId available for replay confirmation');

            const entityIds = entityIdsForEntityType(selectedJob.entityType);
            if (entityIds.length === 0) throw new Error('No experiment entities available for this entity type');

            return trpcClient.screener.optimizer.confirmResult.mutate({
                resultId: input.resultId,
                strategyId: replayStrategyId,
                entityType: selectedJob.entityType,
                entityIds
            });
        },
        onMutate: (input) => {
            setReplayConfirmationPendingResultId(input.resultId);
        },
        onSuccess: (result) => {
            showToast(
                `Queued replay confirmation ${result.confirmation.id} (${result.queueResult.submittedJobs.toLocaleString()} backtests)`,
                'success'
            );

            void confirmationsQuery.refetch();
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to queue replay confirmation', 'error');
        },
        onSettled: () => {
            setReplayConfirmationPendingResultId(null);
        }
    });

    const confirmBundleMutation = useMutation({
        mutationFn: () => {
            if (!selectedJob) throw new Error('Select an optimizer job first');

            const confirmationStrategyId = selectedJob.strategyId ?? strategyId ?? null;
            if (!confirmationStrategyId) throw new Error('No strategyId available for optimizer confirmation');

            const resultIds = selectedBundleResultIdsByRank;
            if (resultIds.length < 2) throw new Error('Select at least 2 results to confirm an OR bundle');

            const entityIds = entityIdsForEntityType(selectedJob.entityType);
            if (entityIds.length === 0) throw new Error('No experiment entities available for this entity type');

            return trpcClient.screener.optimizer.confirmBundle.mutate({
                jobId: selectedJob.id,
                resultIds,
                strategyId: confirmationStrategyId,
                entityType: selectedJob.entityType,
                entityIds
            });
        },
        onSuccess: (result) => {
            showToast(
                `Queued OR-bundle confirmation ${result.confirmation.id} (${result.queueResult.submittedJobs.toLocaleString()} backtests)`,
                'success'
            );

            void confirmationsQuery.refetch();
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to queue OR-bundle confirmation', 'error');
        }
    });

    const preflightReadyToCreate =
        !preflightEnabled || (preflight?.canCreateJob === true && !preflightQuery.isFetching && !preflightQuery.isError);

    const canCreateJob =
        executionStatus === 'completed' &&
        supportedEntityTypes.length > 0 &&
        supportedEntityTypes.includes(entityType) &&
        duration.length > 0 &&
        Number.isFinite(horizonSeconds) &&
        horizonSeconds > 0 &&
        (outcomeMode === 'fixed_horizon' ||
            (parseOptionalPositiveInt(strategyExitStrategyId) !== undefined &&
                parseOptionalPositiveInt(strategyExitCandidateId) !== undefined)) &&
        preflightReadyToCreate &&
        !createJobMutation.isPending;

    const toggleBundleSelectedResultId = useCallback(
        (resultId: number) => {
            const result = resultById.get(resultId);
            if (result) bundleResultCacheRef.current.set(resultId, result);

            setBundleSelectedResultIds((prev) => {
                const next = new Set(prev);

                if (next.has(resultId)) {
                    next.delete(resultId);
                } else {
                    next.add(resultId);
                }

                return next;
            });
        },
        [resultById]
    );

    const clearBundleSelection = useCallback(() => {
        bundleResultCacheRef.current.clear();
        setBundleSelectedResultIds(new Set());
    }, []);

    const selectBundleResultIds = useCallback(
        (resultIds: number[]) => {
            const nextIds = new Set(resultIds);
            bundleResultCacheRef.current.clear();

            for (const resultId of nextIds) {
                const result = resultById.get(resultId);
                if (result) bundleResultCacheRef.current.set(resultId, result);
            }

            setBundleSelectedResultIds(nextIds);
        },
        [resultById]
    );

    const openPromoteModal = useCallback(
        (result: OptimizerResultRow) => {
            setPromoteModalResult(result);

            setPromotionName(
                `Optimizer - ${experimentName} - score ${result.selectionScore !== null ? formatDecimal(result.selectionScore, 4) : '?'} (id ${
                    result.id
                })`
            );

            setPromotionDescription(
                `Promoted from optimizer result (experimentId=${experimentId}, jobId=${result.jobId}, resultId=${result.id}).`
            );

            setPromotionIsActive(true);
            setPromotionAttachToStrategy(false);
        },
        [experimentId, experimentName, strategyId]
    );

    const handleConfirmStrategyExitReplay = useCallback(
        (result: OptimizerResultRow) => {
            if (!selectedJob) {
                showToast('Select an optimizer job first', 'warning');
                return;
            }

            if (result.jobId !== selectedJob.id) {
                showToast('Select the optimizer job that owns this result first', 'warning');
                return;
            }

            if (selectedJob.outcomeMode !== 'strategy_exit') {
                showToast('Replay confirmation is only available for strategy-exit jobs', 'warning');
                return;
            }

            confirmStrategyExitReplayMutation.mutate({ resultId: result.id });
        },
        [confirmStrategyExitReplayMutation, selectedJob]
    );

    const openPromoteBundleModal = useCallback(
        (resultIds: number[]) => {
            if (selectedJobId === null) {
                showToast('Select an optimizer job first', 'warning');
                return;
            }

            const unique = Array.from(new Set(resultIds));

            if (unique.length < 2) {
                showToast('Select at least 2 results to create a bundle', 'warning');
                return;
            }

            unique.sort((a, b) => {
                const scoreA = toNumber(resultById.get(a)?.selectionScore);
                const scoreB = toNumber(resultById.get(b)?.selectionScore);
                if (scoreA !== scoreB) return scoreB - scoreA;
                return a - b;
            });

            setBundlePromoteModalResultIds(unique);
            setBundlePromotionName(`Optimizer OR Bundle - ${experimentName} - job ${selectedJobId} (${unique.length} rules)`);

            setBundlePromotionDescription(
                `Promoted from optimizer bundle (experimentId=${experimentId}, jobId=${selectedJobId}, resultIds=[${unique.join(',')}]).`
            );

            setBundlePromotionIsActive(true);
            setBundlePromotionAttachToStrategy(false);
        },
        [experimentId, experimentName, resultById, selectedJobId, strategyId]
    );

    const handlePromote = async () => {
        if (!promoteModalResult) return;

        if (!promotionName.trim()) {
            showToast('Name is required', 'error');
            return;
        }

        const result = await promoteResultMutation
            .mutateAsync({
                resultId: promoteModalResult.id,
                name: promotionName.trim(),
                description: promotionDescription.trim().length > 0 ? promotionDescription.trim() : null,
                isActive: promotionIsActive
            })
            .catch(() => null);

        if (!result) return;

        showToast(
            result.alreadyPromoted ? `Already promoted (ruleId=${result.screenerRuleId})` : `Promoted (ruleId=${result.screenerRuleId})`,
            'success'
        );

        void resultsQuery.refetch();

        if (promotionAttachToStrategy) {
            const attachResult = await attachToStrategyMutation.mutateAsync({ screenerRuleId: result.screenerRuleId }).catch(() => null);

            if (attachResult?.success) {
                const confirmationNotice = optimizerConfirmationNotice(attachResult.optimizerConfirmationAdvisory);

                showToast(
                    confirmationNotice
                        ? `Attached to strategy (ruleId=${result.screenerRuleId}). ${confirmationNotice}`
                        : `Attached to strategy (ruleId=${result.screenerRuleId})`,
                    confirmationNotice ? 'warning' : 'success'
                );

                setPromoteModalResult(null);
            } else {
                showToast(attachResult?.errorMessages[0] ?? 'Promoted successfully, but failed to attach to strategy.', 'warning');
            }

            return;
        }

        setPromoteModalResult(null);
    };

    const handlePromoteBundle = async () => {
        if (bundlePromoteModalResultIds === null) return;
        if (selectedJobId === null) return;

        if (!bundlePromotionName.trim()) {
            showToast('Name is required', 'error');
            return;
        }

        const result = await promoteBundleMutation
            .mutateAsync({
                jobId: selectedJobId,
                resultIds: bundlePromoteModalResultIds,
                name: bundlePromotionName.trim(),
                description: bundlePromotionDescription.trim().length > 0 ? bundlePromotionDescription.trim() : null,
                isActive: bundlePromotionIsActive
            })
            .catch(() => null);

        if (!result) return;

        const insertedCount = result.insertedPromotionResultIds.length;
        const alreadyCount = result.alreadyPromotedResultIds.length;

        showToast(
            `Bundle created (ruleId=${result.screenerRuleId}, insertedPromotions=${insertedCount}, alreadyPromoted=${alreadyCount})`,
            'success'
        );

        if (bundlePromotionAttachToStrategy) {
            const attachResult = await attachToStrategyMutation.mutateAsync({ screenerRuleId: result.screenerRuleId }).catch(() => null);

            if (attachResult?.success) {
                const confirmationNotice = optimizerConfirmationNotice(attachResult.optimizerConfirmationAdvisory);

                showToast(
                    confirmationNotice
                        ? `Attached to strategy (ruleId=${result.screenerRuleId}). ${confirmationNotice}`
                        : `Attached to strategy (ruleId=${result.screenerRuleId})`,
                    confirmationNotice ? 'warning' : 'success'
                );

                setBundlePromoteModalResultIds(null);
                clearBundleSelection();
            } else {
                showToast(attachResult?.errorMessages[0] ?? 'Bundle created, but failed to attach to strategy.', 'warning');
            }

            return;
        }

        setBundlePromoteModalResultIds(null);
        clearBundleSelection();
    };

    const handlePreviewSelectedBundle = useCallback(() => {
        if (selectedJobId === null) {
            showToast('Select an optimizer job first', 'warning');
            return;
        }

        if (selectedBundleResultIdsByRank.length < 2) {
            showToast('Select at least 2 results', 'warning');
            return;
        }

        previewBundleMetricsMutation.mutate({ jobId: selectedJobId, resultIds: selectedBundleResultIdsByRank });
    }, [previewBundleMetricsMutation, selectedBundleResultIdsByRank, selectedJobId]);

    const handleSuggestBundles = useCallback(() => {
        if (selectedJobId === null) {
            showToast('Select an optimizer job first', 'warning');
            return;
        }

        suggestBundlesMutation.mutate({
            jobId: selectedJobId,
            candidateLimit: 120,
            maxBundles: 6,
            maxRulesPerBundle: 6,
            ...(minTradeCount !== undefined ? { minTradeCount } : {}),
            ...(minHoldoutTradeCount !== undefined ? { minHoldoutTradeCount } : {}),
            ...(minOptionTradeCount !== undefined ? { minOptionTradeCount } : {}),
            ...(minOptionExecutionRate !== undefined ? { minOptionExecutionRate } : {}),
            minHoldoutLift: Math.max(0, minHoldoutLift ?? 0)
        });
    }, [
        minHoldoutLift,
        minHoldoutTradeCount,
        minOptionExecutionRate,
        minOptionTradeCount,
        minTradeCount,
        selectedJobId,
        suggestBundlesMutation
    ]);

    const handleRunAudit = useCallback(
        (profile: AuditProfile) => {
            if (selectedJobId === null) {
                showToast('Select an optimizer job first', 'warning');
                return;
            }

            if (selectedJob?.status !== 'completed') {
                showToast('Audit is available after the optimizer job completes', 'warning');
                return;
            }

            auditPackMutation.mutate({
                jobId: selectedJobId,
                profile,
                candidateLimit: quickAuditCandidateLimit,
                robustLimit: quickAuditRobustLimit
            });
        },
        [auditPackMutation, selectedJob?.status, selectedJobId]
    );

    const handleStartExternalAuditSession = useCallback(() => {
        if (selectedJobId === null) {
            showToast('Select an optimizer job first', 'warning');
            return;
        }

        if (selectedJob?.status !== 'completed') {
            showToast('AI screener recommendation is available after the optimizer job completes', 'warning');
            return;
        }

        const trimmedUserGuidance = userGuidance.trim();

        if (trimmedUserGuidance.length > 0 && trimmedUserGuidance.length < 20) {
            showToast('Screener guidance must be at least 20 characters', 'warning');
            return;
        }

        const normalizedRetentionTarget = targetSignalRetentionPct.trim();
        const normalizedRetentionTolerance = targetSignalRetentionTolerancePct.trim();
        let retentionTarget: number | null = null;
        let retentionTolerance: number | null = null;

        if (normalizedRetentionTarget) {
            retentionTarget = Number(normalizedRetentionTarget);

            if (!Number.isInteger(retentionTarget) || retentionTarget < 1 || retentionTarget > 100) {
                showToast('Signal-retention target must be a whole percentage from 1 to 100', 'warning');
                return;
            }

            retentionTolerance = normalizedRetentionTolerance
                ? Number(normalizedRetentionTolerance)
                : Math.min(10, retentionTarget, 100 - retentionTarget);

            if (
                !Number.isInteger(retentionTolerance) ||
                retentionTolerance < 0 ||
                retentionTolerance > 50 ||
                retentionTarget - retentionTolerance < 0 ||
                retentionTarget + retentionTolerance > 100
            ) {
                showToast('Signal-retention tolerance must keep the target range within 0–100%', 'warning');
                return;
            }
        }

        externalAuditSessionMutation.mutate({
            jobId: selectedJobId,
            analysisObjective,
            ...(trimmedUserGuidance ? { userGuidance: trimmedUserGuidance } : {}),
            ...(retentionTarget !== null
                ? {
                      targetSignalRetentionPct: retentionTarget,
                      targetSignalRetentionTolerancePct: retentionTolerance ?? 0
                  }
                : {}),
            candidateLimit: externalAiAuditCandidateLimit,
            robustLimit: externalAiAuditRobustLimit,
            externalTool: externalAuditTool
        });
    }, [
        analysisObjective,
        externalAuditSessionMutation,
        externalAuditTool,
        selectedJob?.status,
        selectedJobId,
        targetSignalRetentionPct,
        targetSignalRetentionTolerancePct,
        userGuidance
    ]);

    const handleOpenExternalAuditTool = useCallback(() => {
        const launchUrl = externalToolLaunchUrls[externalAuditTool];

        if (!launchUrl) {
            showToast(`${labelForExternalAuditTool(externalAuditTool)} cannot be opened directly from the browser`, 'warning');
            return;
        }

        window.open(launchUrl, '_blank', 'noopener,noreferrer');
    }, [externalAuditTool]);

    const copyTextToClipboard = useCallback((text: string, label: string) => {
        if (!navigator.clipboard) {
            showToast('Clipboard unavailable', 'warning');
            return;
        }

        navigator.clipboard
            .writeText(text)
            .then(() => showToast(`${label} copied`, 'success'))
            .catch(() => showToast(`Failed to copy ${label.toLowerCase()}`, 'error'));
    }, []);

    // --- Column Definitions ---

    // --- Render ---

    return (
        <>
            <JobConfigCard
                availableHorizons={availableHorizonsForSelectedDuration}
                binCount={binCount}
                canCreateJob={canCreateJob}
                createJobPending={createJobMutation.isPending}
                duration={duration}
                durations={durations}
                entityType={entityType}
                executionStatus={executionStatus}
                foldCount={foldCount}
                horizonSeconds={horizonSeconds}
                maxDepth={maxDepth}
                maxFeatures={maxFeatures}
                minSupport={minSupport}
                minUniqueEntities={minUniqueEntities}
                optionObjective={optionObjective}
                outcomeMetric={displayedOutcomeMetric}
                outcomeMode={displayedOutcomeMode}
                optionLegOverrides={optionLegOverrides}
                onOptionLegChange={handleOptionLegChange}
                validationSettings={validationSettings}
                onValidationSettingsChange={setValidationSettings}
                preflight={preflight}
                preflightEnabled={preflightEnabled}
                preflightErrorMessage={preflightQuery.error?.message}
                preflightIsError={preflightQuery.isError}
                preflightIsFetching={preflightQuery.isFetching}
                setBinCount={setBinCount}
                setDuration={setDuration}
                setEntityType={setEntityType}
                setFoldCount={setFoldCount}
                setHorizonSeconds={setHorizonSeconds}
                setMaxDepth={setMaxDepth}
                setMaxFeatures={setMaxFeatures}
                setMinSupport={setMinSupport}
                setMinUniqueEntities={setMinUniqueEntities}
                setOptionObjective={handleOptionObjectiveChange}
                setOutcomeMetric={handleOutcomeMetricChange}
                setOutcomeMode={handleOutcomeModeChange}
                setShowAdvancedConfig={(value) => {
                    setShowAdvancedConfig(value);
                    if (value) setAdvancedConfigEnabled(true);
                }}
                setStrategyExitCandidateId={handleExitCandidateChange}
                setStrategyExitStrategyId={handleStrategyChange}
                showAdvancedConfig={showAdvancedConfig}
                strategyExitCandidateId={
                    outcomeModeUserOverridden ? strategyExitCandidateId : String(outcomeModeRecommendation?.resolvedExitCandidateId ?? '')
                }
                strategyExitStrategyId={
                    outcomeModeUserOverridden
                        ? strategyExitStrategyId
                        : String(outcomeModeRecommendation?.resolvedStrategyId ?? strategyId ?? '')
                }
                supportedEntityTypes={supportedEntityTypes}
                onCreateJob={handleCreateJob}
            />

            {/* Jobs Table */}
            <JobsTableCard
                errorMessage={jobsQuery.error?.message}
                isError={jobsQuery.isError}
                isFetching={jobsQuery.isFetching}
                jobs={jobs}
                selectedJobId={selectedJobId}
                onRefresh={() => {
                    void jobsQuery.refetch();
                }}
                onSelectJob={setSelectedJobId}
            />

            {/* Results / Feature Analysis */}
            {selectedJobId !== null && (
                <Card className="bg-zinc-900 mb-6">
                    <JobDetailHeader
                        auditPackPending={auditPackMutation.isPending}
                        isRefreshing={
                            resultsQuery.isFetching ||
                            featureAnalysisQuery.isFetching ||
                            featureImpactSimpleQuery.isFetching ||
                            featureImpactResultsQuery.isFetching
                        }
                        selectedJob={selectedJob}
                        selectedJobId={selectedJobId}
                        onRefresh={() => {
                            void jobsQuery.refetch();
                            void resultsQuery.refetch();
                            void confirmationsQuery.refetch();

                            if (selectedJob?.status === 'completed') {
                                void optionTimingEvidenceQuery.refetch();
                            }

                            if (activeTab === 'features' || featureImpactModalOpen) {
                                void featureAnalysisQuery.refetch();
                            }

                            if (featureImpactModalOpen) {
                                void featureImpactSimpleQuery.refetch();
                            }

                            if (featureImpactModalOpen && featureImpactTab === 'combos') {
                                void featureImpactResultsQuery.refetch();
                            }
                        }}
                        onRunAudit={handleRunAudit}
                    />

                    {selectedJob?.status === 'failed' && (
                        <div className="text-red-300 text-sm mb-3">
                            Job failed{selectedJob.errorMessage ? `: ${selectedJob.errorMessage}` : '.'}
                        </div>
                    )}

                    {selectedJob && (
                        <JobStatsGrid
                            holdoutInfoText={holdoutInfoText}
                            results={results}
                            resultsTotalCount={resultsTotalCount}
                            selectedJob={selectedJob}
                            selectedJobProgress={selectedJobProgress}
                            selectedJobUsesOptionMetric={selectedJobUsesOptionMetric}
                        />
                    )}

                    {activeAuditPack && (
                        <AuditSummaryBanner activeAuditPack={activeAuditPack} onShowDetails={() => setAuditModalOpen(true)} />
                    )}

                    <OptimizerConfirmationsPanel
                        confirmations={confirmations}
                        errorMessage={confirmationsQuery.error?.message}
                        isError={confirmationsQuery.isError}
                        isFetching={confirmationsQuery.isFetching}
                        onRefresh={() => {
                            void confirmationsQuery.refetch();
                        }}
                    />

                    {selectedJob?.status === 'completed' && (
                        <ExternalAuditSection
                            activeExternalAnalysisRun={activeExternalAnalysisRun}
                            activeExternalAnalysisPlan={activeExternalAnalysisPlan}
                            activeExternalPrompt={activeExternalPrompt}
                            activeExternalRecommendations={activeExternalRecommendations}
                            walkForwardEvaluations={walkForwardEvaluations}
                            experimentWalkForwardExposure={experimentWalkForwardExposure}
                            activeExternalWorkbench={activeExternalWorkbench}
                            analysisObjective={analysisObjective}
                            decidePromotionApprovalPending={decidePromotionApprovalMutation.isPending}
                            externalApprovalByArtifactId={externalApprovalByArtifactId}
                            externalAuditSessionPending={externalAuditSessionMutation.isPending}
                            externalAuditTool={externalAuditTool}
                            jobRequestApprovalByArtifactId={jobRequestApprovalByArtifactId}
                            userGuidance={userGuidance}
                            targetSignalRetentionPct={targetSignalRetentionPct}
                            targetSignalRetentionTolerancePct={targetSignalRetentionTolerancePct}
                            optionTimingEvidenceLoading={optionTimingEvidenceLoading}
                            optionTimingEvidenceReadiness={optionTimingEvidenceReadiness}
                            selectedJobUsesOptionEv={selectedJobUsesOptionEv}
                            selectedJobUsesOptionMetric={selectedJobUsesOptionMetric}
                            selectedJob={selectedJob}
                            selectedMcpReadiness={selectedMcpReadiness}
                            setAnalysisObjective={setAnalysisObjectiveOverride}
                            setExternalAuditTool={setExternalAuditTool}
                            setUserGuidance={setUserGuidance}
                            setTargetSignalRetentionPct={setTargetSignalRetentionPct}
                            setTargetSignalRetentionTolerancePct={setTargetSignalRetentionTolerancePct}
                            startApprovedOptimizerJobPending={startApprovedOptimizerJobMutation.isPending}
                            onCopyText={copyTextToClipboard}
                            onDecidePromotionApproval={(input) => decidePromotionApprovalMutation.mutate(input)}
                            onOpenExternalAuditTool={handleOpenExternalAuditTool}
                            onStartApprovedOptimizerJob={(artifactId) => startApprovedOptimizerJobMutation.mutate(artifactId)}
                            onStartExternalAuditSession={handleStartExternalAuditSession}
                        />
                    )}

                    <SegmentedControl
                        className="mb-3"
                        options={[
                            {
                                value: 'results',
                                label: resultsTotalCount > 0 ? `Results · ${resultsTotalCount.toLocaleString()}` : 'Results'
                            },
                            { value: 'features', label: 'Feature analysis' }
                        ]}
                        value={activeTab}
                        onChange={setActiveTab}
                    />

                    {activeTab === 'results' && (
                        <ResultsSection
                            activeFilters={activeFilters}
                            auditCandidateByResultId={auditCandidateByResultId}
                            auditPackPending={auditPackMutation.isPending}
                            bundleSelectedResultIds={bundleSelectedResultIds}
                            clearBundleSelection={clearBundleSelection}
                            clearFilters={clearFilters}
                            confirmBundlePending={confirmBundleMutation.isPending}
                            displayIndexByResultId={displayIndexByResultId}
                            filterValues={filterValues}
                            filtersDrawerOpen={filtersDrawerOpen}
                            hiddenSimilarCount={hiddenSimilarCount}
                            hideSimilarResults={hideSimilarResults}
                            hideSimilarThreshold={hideSimilarThreshold}
                            holdoutFractionLabel={holdoutFractionLabel}
                            openPromoteBundleModal={openPromoteBundleModal}
                            openPromoteModal={openPromoteModal}
                            previewBundlePending={previewBundleMetricsMutation.isPending}
                            replayConfirmationPendingResultId={replayConfirmationPendingResultId}
                            results={results}
                            resultsErrorMessage={resultsQuery.error?.message}
                            resultsFilteredForDiversity={resultsFilteredForDiversity}
                            resultsIsError={resultsQuery.isError}
                            resultsIsFetching={resultsQuery.isFetching}
                            resultsPage={resultsPage}
                            resultsPageSize={OPTIMIZER_RESULTS_PAGE_SIZE}
                            resultsSorting={resultsSorting}
                            resultsTotalCount={resultsTotalCount}
                            selectedBundleResultIdsByRank={selectedBundleResultIdsByRank}
                            selectedJobId={selectedJobId}
                            selectedJobOutcomeMode={selectedJob?.outcomeMode}
                            selectedJobStatus={selectedJob?.status}
                            selectedJobUsesOptionEv={selectedJobUsesOptionEv}
                            selectedJobUsesOptionMetric={selectedJobUsesOptionMetric}
                            setFilterValue={setFilterValue}
                            setFiltersDrawerOpen={setFiltersDrawerOpen}
                            setHideSimilarResults={setHideSimilarResults}
                            setHideSimilarThreshold={setHideSimilarThreshold}
                            setResultsPage={setResultsPage}
                            setResultsSorting={setResultsSorting}
                            suggestBundlesPending={suggestBundlesMutation.isPending}
                            toggleBundleSelectedResultId={toggleBundleSelectedResultId}
                            visibleFilters={visibleFilters}
                            onConfirmReplay={handleConfirmStrategyExitReplay}
                            onConfirmSelectedBundle={() => confirmBundleMutation.mutate()}
                            onPreviewSelectedBundle={handlePreviewSelectedBundle}
                            onShowFolds={setFoldsModalResultId}
                            onShowRuleJson={setRuleJsonModalResult}
                            onSuggestBundles={handleSuggestBundles}
                        />
                    )}

                    {activeTab === 'features' && (
                        <FeaturesSection
                            errorMessage={featureAnalysisQuery.error?.message}
                            features={features}
                            isError={featureAnalysisQuery.isError}
                            isFetching={featureAnalysisQuery.isFetching}
                            onOpenFeatureImpact={() => {
                                setFeatureImpactSelectedFeatureKey(null);
                                setFeatureImpactTab('needleMovers');
                                setFeatureImpactModalOpen(true);
                            }}
                        />
                    )}
                </Card>
            )}

            {/* Robust Audit Modal */}
            <RobustAuditModal
                activeAuditPack={activeAuditPack}
                activeExternalPrompt={activeExternalPrompt}
                externalAuditSessionPending={externalAuditSessionMutation.isPending}
                isOpen={auditModalOpen}
                optionTimingEvidenceLoading={optionTimingEvidenceLoading}
                optionTimingEvidenceReadiness={optionTimingEvidenceReadiness}
                selectedJobUsesOptionMetric={selectedJobUsesOptionMetric}
                onClose={() => setAuditModalOpen(false)}
                onCopyText={copyTextToClipboard}
                onStartExternalAuditSession={handleStartExternalAuditSession}
            />

            {/* Feature Impact Modal */}
            <FeatureImpactModal
                featureAnalysisErrorMessage={featureAnalysisQuery.error?.message}
                featureAnalysisIsError={featureAnalysisQuery.isError}
                featureAnalysisIsFetching={featureAnalysisQuery.isFetching}
                featureImpactSelectedFeatureKey={featureImpactSelectedFeatureKey}
                featureImpactSimpleErrorMessage={featureImpactSimpleQuery.error?.message}
                featureImpactSimpleIsError={featureImpactSimpleQuery.isError}
                featureImpactSimpleIsFetching={featureImpactSimpleQuery.isFetching}
                featureImpactResultsErrorMessage={featureImpactResultsQuery.error?.message}
                featureImpactResultsIsError={featureImpactResultsQuery.isError}
                featureImpactResultsIsFetching={featureImpactResultsQuery.isFetching}
                featureImpactTab={featureImpactTab}
                features={features}
                isOpen={featureImpactModalOpen}
                needleMovers={needleMovers}
                results={featureImpactResults}
                selectedJobId={selectedJobId}
                setFeatureImpactSelectedFeatureKey={setFeatureImpactSelectedFeatureKey}
                setFeatureImpactTab={setFeatureImpactTab}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onClose={() => setFeatureImpactModalOpen(false)}
            />

            {/* Rule JSON Modal */}
            <RuleJsonModal
                result={ruleJsonModalResult}
                selectedJobUsesOptionEv={selectedJobUsesOptionEv}
                selectedJobUsesOptionMetric={selectedJobUsesOptionMetric}
                onClose={() => setRuleJsonModalResult(null)}
            />

            {/* Folds Modal */}
            <FoldsModal resultId={foldsModalResultId} onClose={() => setFoldsModalResultId(null)} />

            {/* Promote Modal */}
            <PromoteResultModal
                isWorking={promoteResultMutation.isPending || attachToStrategyMutation.isPending}
                promotionAttachToStrategy={promotionAttachToStrategy}
                promotionDescription={promotionDescription}
                promotionIsActive={promotionIsActive}
                promotionName={promotionName}
                result={promoteModalResult}
                setPromotionAttachToStrategy={setPromotionAttachToStrategy}
                setPromotionDescription={setPromotionDescription}
                setPromotionIsActive={setPromotionIsActive}
                setPromotionName={setPromotionName}
                strategyId={strategyId}
                onClose={() => setPromoteModalResult(null)}
                onPromote={handlePromote}
            />

            {/* Bundle Preview Modal */}
            <BundlePreviewModal
                data={bundlePreviewModalData}
                onClose={() => setBundlePreviewModalData(null)}
                onCreateBundle={() => openPromoteBundleModal(selectedBundleResultIdsByRank)}
            />

            {/* Promote Bundle Modal */}
            <PromoteBundleModal
                bundlePromotionAttachToStrategy={bundlePromotionAttachToStrategy}
                bundlePromotionDescription={bundlePromotionDescription}
                bundlePromotionIsActive={bundlePromotionIsActive}
                bundlePromotionName={bundlePromotionName}
                isWorking={promoteBundleMutation.isPending || attachToStrategyMutation.isPending}
                resultIds={bundlePromoteModalResultIds}
                selectedJobId={selectedJobId}
                setBundlePromotionAttachToStrategy={setBundlePromotionAttachToStrategy}
                setBundlePromotionDescription={setBundlePromotionDescription}
                setBundlePromotionIsActive={setBundlePromotionIsActive}
                setBundlePromotionName={setBundlePromotionName}
                strategyId={strategyId}
                onClose={() => setBundlePromoteModalResultIds(null)}
                onPromote={handlePromoteBundle}
            />

            {/* Confirmation Backtest Pack Modal */}
            <ConfirmationPackModal
                availableEntityCount={confirmationPackEntityIds.length}
                bypassCache={confirmationPackBypassCache}
                isQueueing={queueConfirmationPackMutation.isPending}
                maxEntities={confirmationPackMaxEntities}
                setBypassCache={setConfirmationPackBypassCache}
                setMaxEntities={setConfirmationPackMaxEntities}
                target={confirmationPackTarget}
                onClose={() => setConfirmationPackTarget(null)}
                onQueue={() => queueConfirmationPackMutation.mutate()}
            />

            {/* Suggested Bundles Modal */}
            <SuggestedBundlesModal
                isOpen={suggestBundlesModalOpen}
                isPending={suggestBundlesMutation.isPending}
                selectedJobId={selectedJobId}
                suggestions={suggestedBundles}
                onClose={() => setSuggestBundlesModalOpen(false)}
                onCreateBundle={(resultIds) => openPromoteBundleModal(resultIds)}
                onSelectBundle={selectBundleResultIds}
            />
        </>
    );
};
